import type { AdminPackageDetail } from '../../types/packages';

/**
 * Spec 081b — the package policy checklist, computed from the detail (design §4 Admin):
 * cover 2:1 ≥ 800 px, no contact details, saving ≥ 5 %, 2–6 items, Arabic present when required.
 * A rubric for the reviewer: nothing here is sent or gates the decision; the server stays authoritative.
 */

export const MIN_SAVING_PERCENT = 5;
export const MIN_ITEMS = 2;
export const MAX_ITEMS = 6;
export const COVER_MIN_WIDTH = 800;

/** pass / fail, or `check` while it cannot be decided yet (cover still loading). */
export type PolicyResult = 'pass' | 'fail' | 'check';

export interface PolicyRow {
  id: 'cover' | 'contact' | 'saving' | 'items' | 'arabic';
  rule: string;
  result: PolicyResult;
  hint: string;
}

export interface ImageSize {
  width: number;
  height: number;
}

const CONTACT_PATTERNS: ReadonlyArray<RegExp> = [
  /[\w.+-]+@[\w-]+\.[a-z]{2,}/i, // email
  /(https?:\/\/|www\.)\S+/i, // url
  /\b[\w-]+\.(com|ae|net|org|io|co|me)\b/i, // bare domain
  /\b(whats\s?app|wa\.me|instagram|insta|snapchat|tiktok|telegram)\b/i,
  /(^|\s)@[A-Za-z0-9_.]{3,}/, // social handle
  /واتس|واتساب|انستغرام|اتصل/,
];

/** A run of digits, spaces and dashes holding at least 9 digits (Latin or Arabic-Indic) reads as a phone number. */
function hasPhoneNumber(text: string): boolean {
  const runs = text.match(/\+?[\d٠-٩][\d٠-٩\s-]{6,}[\d٠-٩]/g) ?? [];
  return runs.some((run) => (run.match(/[\d٠-٩]/g) ?? []).length >= 9);
}

export function containsContactDetails(...texts: Array<string | null | undefined>): boolean {
  const text = texts.filter(Boolean).join(' ');
  return hasPhoneNumber(text) || CONTACT_PATTERNS.some((p) => p.test(text));
}

export function coverCheck(hasCover: boolean, size: ImageSize | null): Pick<PolicyRow, 'result' | 'hint'> {
  if (!hasCover) return { result: 'pass', hint: 'No cover: the app shows the navy card.' };
  if (!size) return { result: 'check', hint: 'Reading the cover size…' };
  const ratio = size.width / size.height;
  if (Math.abs(ratio - 2) > 0.1) return { result: 'fail', hint: `Cover is ${size.width}×${size.height}, not 2:1.` };
  if (size.width < COVER_MIN_WIDTH) return { result: 'fail', hint: `Cover is only ${size.width}px wide (needs ${COVER_MIN_WIDTH}).` };
  return { result: 'pass', hint: `Cover is ${size.width}×${size.height} (2:1).` };
}

export function buildPolicyRows(detail: AdminPackageDetail, coverSize: ImageSize | null): PolicyRow[] {
  const contact = detail.contactDetailsInText
    || containsContactDetails(detail.nameEn, detail.nameAr, detail.descriptionEn, detail.descriptionAr);
  const saving = detail.savingPercent ?? (detail.pricingMode === 'PERCENT' ? detail.discountPercent : null);
  const count = detail.items.length;
  const arabicMissing = !detail.nameAr?.trim() || (!!detail.descriptionEn?.trim() && !detail.descriptionAr?.trim());

  return [
    { id: 'cover', rule: `Cover is 2:1 and at least ${COVER_MIN_WIDTH} px wide`, ...coverCheck(!!detail.cover?.downloadUrl, coverSize) },
    {
      id: 'contact',
      rule: 'No phone, WhatsApp, email, link or social handle in the text',
      result: contact ? 'fail' : 'pass',
      hint: contact ? 'A contact pattern was found in the name or description.' : 'No contact pattern in the text. Check the cover image.',
    },
    {
      id: 'saving',
      rule: `Customer saves at least ${MIN_SAVING_PERCENT}%`,
      result: saving != null && saving >= MIN_SAVING_PERCENT ? 'pass' : 'fail',
      hint: saving == null ? 'No saving computed yet.' : `Saving is ${saving}%.`,
    },
    {
      id: 'items',
      rule: `${MIN_ITEMS} to ${MAX_ITEMS} services`,
      result: count >= MIN_ITEMS && count <= MAX_ITEMS ? 'pass' : 'fail',
      hint: `${count} service${count === 1 ? '' : 's'} in the package.`,
    },
    {
      id: 'arabic',
      rule: detail.arabicRequired ? 'Arabic name and description present (required)' : 'Arabic name and description present',
      result: !arabicMissing ? 'pass' : detail.arabicRequired ? 'fail' : 'pass',
      hint: !arabicMissing
        ? 'Arabic provided.'
        : detail.arabicRequired
          ? 'Arabic is required and missing.'
          : 'Arabic is optional and missing: Arabic users see the English text.',
    },
  ];
}
